import { FormControl, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";
import { DekoxIcon } from "src/components";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { Button, IconButton, TooltipShortcut } from "@itk/mui-components";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { configuratorActions } from "../../configurator-store";
import { TSortDirection, TSortField } from "../../configurator-types";

const mapStateToProps = (state: RootState) => ({
  sortField: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.sortField,
  sortDirection: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.sortDirection
});

const ownActions = {
  setSortField: configuratorActions.setSortField,
  setSortDirection: configuratorActions.setSortDirection,
  setActiveOptionsPanel: configuratorActions.setActiveOptionsPanel
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const SortOptionsBase = (props: Props) => {
  const { sortField, setSortField, sortDirection, setSortDirection, setActiveOptionsPanel } = props;

  const configuratorPageT = useConfiguratorPageT();
  const genericTranslationsT = useGenericTranslationsT();

  // Back to configuratorReducer's initialState defaults for these two fields — selections still
  // apply instantly, Reset is just a shortcut to the default combination rather than an Apply/Reset lifecycle
  const handleReset = () => {
    // Products tab now defaults to "none" — no client-side sort, keeps the API's own returned order
    setSortField("none");
    setSortDirection("asc");
  };

  return (
    <Stack overflow="hidden" width="100%" py={2} flex={1} minHeight={0}>
      {/* Header: title + Reset + close icon — selections still apply instantly, Reset just jumps to defaults */}
      <Stack flexShrink={0} px={3} direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" fontWeight={600}>
          {configuratorPageT.sortOptions}
        </Typography>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Button color="error" t={genericTranslationsT} k={genericTranslationsK} field="reset" variant="text" size="small" onClick={handleReset} />
          <TooltipShortcut shortcut={{ action: () => setActiveOptionsPanel(null), shortCutKey: "s", modifier: "alt" }}>
            <IconButton k={genericTranslationsK.close} size="small" onClick={() => setActiveOptionsPanel(null)}>
              <DekoxIcon icon="doubleArrowRight" fontSize="small" />
            </IconButton>
          </TooltipShortcut>
        </Stack>
      </Stack>
      <Stack overflow="auto" flex={1} minHeight={0} gap={2} mt={2} px={3} py={2}>
        {/* field selector: which column to sort by */}
        <FormControl size="small" fullWidth>
          <InputLabel>{configuratorPageT.sortBy}</InputLabel>
          <Select value={sortField} label={configuratorPageT.sortBy} onChange={e => setSortField(e.target.value as TSortField)}>
            {/* "none" (API's own order, the default) listed first, matching the Configurations tab */}
            <MenuItem value="none">{configuratorPageT.sortByNone}</MenuItem>
            <MenuItem value="productDescription">{configuratorPageT.sortByProductName}</MenuItem>
            <MenuItem value="productNumber">{configuratorPageT.sortByProductNumber}</MenuItem>
            <MenuItem value="productTypeName">{configuratorPageT.sortByProductType}</MenuItem>
            <MenuItem value="configurationName">{configuratorPageT.sortByConfigurationName}</MenuItem>
            <MenuItem value="createdOn">{configuratorPageT.sortByCreatedOn}</MenuItem>
            <MenuItem value="modifiedOn">{configuratorPageT.sortByModifiedOn}</MenuItem>
          </Select>
        </FormControl>
        {/* direction is meaningless when no sort field is applied, so hide it entirely for "none" */}
        {sortField !== "none" && (
          <FormControl size="small" fullWidth>
            <InputLabel>{configuratorPageT.sortOrder}</InputLabel>
            <Select value={sortDirection} label={configuratorPageT.sortOrder} onChange={e => setSortDirection(e.target.value as TSortDirection)}>
              <MenuItem value="asc">{genericTranslationsT.sortAscending}</MenuItem>
              <MenuItem value="desc">{genericTranslationsT.sortDescending}</MenuItem>
            </Select>
          </FormControl>
        )}
      </Stack>
    </Stack>
  );
};

export const SortOptions = connect(mapStateToProps, ownActions)(SortOptionsBase);
